import BadgeRenderer from '@/app/common/components/CellRenderers/BadgeRenderer';
import URLRenderer from '@/app/common/components/CellRenderers/URLRenderer';
import { getColor } from '@/helpers/utils';
 
import ProtocolRenderer from '@/app/common/components/CellRenderers/ProtocolRenderer';
import BooleanRenderer from '@/app/common/components/CellRenderers/BooleanRenderer';
import StatusCodeRenderer from '@/app/common/components/CellRenderers/StatusCodeRenderer';
import { crawlTooltip } from '@/app/Crawl/tooltip/crawlTooltips';
import typeToDetail from '@/helpers/typeToDetail';

import type { ICellRendererParams } from 'ag-grid-community';
import type { ReactNode } from 'react';

type CrawlRow = Record<string, any>;
type CrawlCellParams = Omit<ICellRendererParams<CrawlRow, any>, 'data'> & {
  data: CrawlRow;
};
type CrawlColumnProps = Record<string, unknown>;
type CrawlColumn = {
  [key: string]: unknown;
  cellRenderer?: (params: CrawlCellParams) => ReactNode;
};

export const urlColumn = (
  props: CrawlColumnProps = {}
): CrawlColumn => ({
  gp: 'نشانی (آدرس صفحه)',
  field: 'url',
  type: 'string',
  title: 'نشانی',
  minWidth: 150,
  pinned: 'right',
  searchable: true,
  cellRenderer: (params: CrawlCellParams) => {
    return (
      <URLRenderer
        url={params.data.url}
        job_page={params.data.job_page}
        status_code={params.data.status_code}
        type={params.data.type || ''}
        queries={['issues']}
        details={typeToDetail(params.data.type) || 'internal'}
        activeTab={
          params.data.type
            ? typeToDetail(params.data.type) === 'internal'
              ? 'overview'
              : typeToDetail(params.data.type)
            : 'overview'
        }
        {...props}
      />
    );
  }
});

export const content_typeColumn = {
  gp: 'اطلاعات کلی از صفحه',
  field: 'content_type',
  type: 'string',
  title: 'نوع محتوا',
  searchable: true,
  tooltip: crawlTooltip.columns.content_type
};

export const langColumn = {
  gp: 'اطلاعات کلی از صفحه',
  field: 'lang',
  type: 'string',
  searchable: true,
  width: 125,
  title: 'زبان صفحه',
  tooltip: crawlTooltip.columns.lang
};

export const ps_urlColumn = (
  props: CrawlColumnProps = {}
): CrawlColumn => ({
  gp: 'اطلاعات صفحه',
  width: 350,
  field: 'ps_url',
  type: 'string',
  title: 'نشانی صفحه',
  pinned: 'right',
  searchable: true,
  cellRenderer: (params: CrawlCellParams) => {
    const { data } = params;
    if (!data) return null;

    return (
      <URLRenderer
        url={data.ps_url}
        job_page={data.job_page}
        status_code={data.status_code}
        type={data.type || ''}
        queries={['issues']}
        details={typeToDetail(data.type) || 'internal'}
        activeTab={
          data.type
            ? typeToDetail(data.type) === 'internal'
              ? 'overview'
              : typeToDetail(data.type)
            : 'overview'
        }
        {...props}
      />
    );
  }
});

export const ps_url_depthColumn = {
  gp: 'اطلاعات صفحه',
  width: 140,
  field: 'depth',
  type: 'number',
  title: 'عمق صفحه',
  searchable: true
};

export const protocolColumn = {
  gp: 'اطلاعات صفحه',
  width: 150,
  field: 'protocol',
  type: 'string',
  title: 'پروتکل صفحه',
  searchable: true,
  tooltip: crawlTooltip.columns.protocol,
  cellRenderer: (params: CrawlCellParams) => (
    <ProtocolRenderer protocol={params.data.protocol} />
  )
};

export const domain_linkColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  width: 150,
  field: 'domain_link',
  type: 'string',
  title: 'دامنه پیوند',
  searchable: true,
  tooltip: crawlTooltip.columns.domain_link
};
export const link_hrefColumn = (
  props: CrawlColumnProps = {}
): CrawlColumn => ({
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 300,
  field: 'link_href',
  type: 'string',
  title: 'نشانی پیوند',
  tooltip: crawlTooltip.columns.link_href,
  cellRenderer: (params: CrawlCellParams) => (
    <URLRenderer
      url={params.data.link_href}
      job_page={params.data.job_page}
      status_code={params.data.status_code}
      type={params.data.type || ''}
      queries={['issues']}
      details={typeToDetail(params.data.type) || 'internal'}
      activeTab={
        params.data.type
          ? typeToDetail(params.data.type) === 'internal'
            ? 'overview'
            : typeToDetail(params.data.type)
          : 'overview'
      }
      {...props}
    />
  )
});
export const link_textColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 150,
  field: 'link_text',
  type: 'string',
  title: 'متن پیوند',
  tooltip: crawlTooltip.columns.link_text
};
export const link_text_badColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 210,
  field: 'link_text_bad',
  type: 'boolean',
  title: 'کلمات غیر مجاز در پیوند؟',
  tooltip: crawlTooltip.columns.link_text_bad,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_text_bad ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_text_bad ? 'داره' : 'نداره'}
    />
  )
};
export const link_href_typeColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 160,
  field: 'link_href_type',
  type: 'string',
  title: 'نوع فایل پیوند',
  tooltip: crawlTooltip.columns.link_href_type
};
export const link_3xx_destinationColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 200,
  field: 'link_3xx_destination',
  type: 'string',
  title: 'پیوند بعد از تغییر مسیر',
  tooltip: crawlTooltip.columns.link_3xx_destination
};
export const link_href_protocolColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 150,
  field: 'link_href_protocol',
  type: 'string',
  title: 'پروتکل پیوند',
  tooltip: crawlTooltip.columns.link_href_protocol,
  cellRenderer: (params: CrawlCellParams) => (
    <ProtocolRenderer protocol={params.data.link_href_protocol} />
  )
};
export const http_links_on_httpsColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 220,
  field: 'http_links_on_https',
  type: 'boolean',
  title: 'پیوند http در صفحه https',
  tooltip: crawlTooltip.columns.http_links_on_https,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_nofollow ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_nofollow ? 'هست' : 'نیست'}
    />
  )
};
export const link_href_fragmentColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 165,
  field: 'link_href_fragment',
  type: 'string',
  title: 'مقدار fragment',
  tooltip: <p>اگر پیوند fragment داشته باشه مقدارش اینجا نمایش داده میشه.</p>
};
export const link_href_queryColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 140,
  field: 'link_href_query',
  type: 'string',
  title: 'مقدار query',
  tooltip: <p>اگر پیوند query داشته باشه مقدارش اینجا نمایش داده میشه.</p>
};
export const link_href_query_countColumn = {
  gp: 'صفحه ارجاع (اطلاعات نشانی که لینک‌ شده)',
  searchable: true,
  width: 140,
  field: 'link_href_query_count',
  type: 'number',
  title: 'تعداد query',
  tooltip: <p>تعداد query پیوند رو میگه.</p>
};
export const link_is_imgColumn = {
  gp: 'اطلاعات متن پیوند عکس دار',
  searchable: true,
  width: 170,
  field: 'link_is_img',
  type: 'boolean',
  title: 'پیوند عکس داره؟',
  tooltip: <p>اگر پیوند صفحه از نوع عکس دار باشه به شما نمایش میده</p>,
  cellRenderer: (params: CrawlCellParams) => (
    <BooleanRenderer boolValue={params.data.link_is_img} />
  )
};
export const link_alt_imgColumn = {
  gp: 'اطلاعات متن پیوند عکس دار',
  searchable: true,
  width: 180,
  field: 'link_alt_img',
  type: 'string',
  title: 'متن جایگزین پیوند',
  tooltip: (
    <p>
      اگر عکس لینک دار باشه و متن جایگزین یا alt داشته باشه ایجاد نمایش داده
      میشه.
    </p>
  )
};
export const link_src_imgColumn = (
  props: CrawlColumnProps = {}
): CrawlColumn => ({
  gp: 'اطلاعات متن پیوند عکس دار',
  searchable: true,
  width: 300,
  field: 'link_src_img',
  type: 'string',
  title: 'نشانی عکس پیوند',
  tooltip: <p>آدرس عکسی که در صفحه لینک شده به شما نمایش میده</p>,
  cellRenderer: (params: CrawlCellParams) => (
    // <URLRenderer //❌
    //   url={params.data.link_src_img}
    //   details="not-internal"
    //   job_page={params.data.job_page}
    //   status_code={params.data.status_code}
    //   activeTab="overview"
    // />
    <URLRenderer
      url={params.data.link_src_img}
      job_page={params.data.job_page}
      status_code={params.data.status_code}
      type={params.data.type || ''}
      queries={['issues']}
      details={typeToDetail(params.data.type) || 'internal'}
      activeTab={
        params.data.type
          ? typeToDetail(params.data.type) === 'internal'
            ? 'overview'
            : typeToDetail(params.data.type)
          : 'overview'
      }
      {...props}
    />
  )
});
export const link_is_nofollowColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 160,
  field: 'link_is_nofollow',
  type: 'boolean',
  title: 'پیوند nofollow',
  tooltip: crawlTooltip.columns.link_is_nofollow,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_nofollow ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_nofollow ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_noopenerColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 160,
  field: 'link_is_noopener',
  type: 'boolean',
  title: 'پیوند noopener',
  tooltip: crawlTooltip.columns.link_is_noopener,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_noopener ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_noopener ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_noreferrerColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 165,
  field: 'link_is_noreferrer',
  type: 'boolean',
  title: 'پیوند noreferrer',
  tooltip: crawlTooltip.columns.link_is_noreferrer,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_noreferrer
          ? getColor('danger')
          : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_noreferrer ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_ugcColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 130,
  field: 'link_is_ugc',
  type: 'boolean',
  title: 'پیوند ugc',
  tooltip: crawlTooltip.columns.link_is_ugc,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_ugc ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_ugc ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_sponsoredColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 160,
  field: 'link_is_sponsored',
  type: 'boolean',
  title: 'پیوند sponsored',
  tooltip: crawlTooltip.columns.link_is_sponsored,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_sponsored
          ? getColor('danger')
          : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_sponsored ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_externalColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 155,
  field: 'link_is_external',
  type: 'boolean',
  title: 'پیوند external',
  tooltip: crawlTooltip.columns.link_is_external,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_external ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_external ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_alternateColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  width: 160,
  field: 'link_is_alternate',
  type: 'boolean',
  title: 'پیوند alternate',
  tooltip: crawlTooltip.columns.link_is_alternate,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_alternate
          ? getColor('danger')
          : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_alternate ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_tagColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 130,
  field: 'link_is_tag',
  type: 'boolean',
  title: 'پیوند tag',
  tooltip: crawlTooltip.columns.link_is_tag,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_tag ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_tag ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_publisherColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  width: 160,
  field: 'link_is_publisher',
  type: 'boolean',
  title: 'پیوند publisher',
  tooltip: crawlTooltip.columns.link_is_publisher,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_publisher
          ? getColor('danger')
          : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_publisher ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_licenseColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 150,
  field: 'link_is_license',
  type: 'boolean',
  title: 'پیوند license',
  tooltip: crawlTooltip.columns.link_is_license,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_license ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_license ? 'هست' : 'نیست'}
    />
  )
};
export const link_is_authorColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 150,
  field: 'link_is_author',
  type: 'boolean',
  title: 'پیوند author',
  tooltip: crawlTooltip.columns.link_is_author,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_author ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_author ? 'هست' : 'نیست'}
    />
  )
};

export const link_is_bookmarkColumn = {
  gp: 'ویژگی پیوند (rel لینک)',
  searchable: true,
  width: 165,
  field: 'link_is_bookmark',
  type: 'boolean',
  title: 'پیوند bookmark',
  tooltip: crawlTooltip.columns.link_is_bookmark,
  cellRenderer: (params: CrawlCellParams) => (
    <BadgeRenderer
      color={
        params.data.link_is_bookmark ? getColor('danger') : getColor('gray-600')
      }
      textColor={getColor('white')}
      text={params.data.link_is_bookmark ? 'هست' : 'نیست'}
    />
  )
};
export const status_codeColumn = {
  gp: 'اطلاعات کلی از صفحه',
  field: 'status_code',
  type: 'number',
  width: 130,
  title: 'کد وضعیت',
  cellRenderer: (params: CrawlCellParams) => (
    <StatusCodeRenderer statusCode={params.data.status_code} />
  )
};
export const size_KBColumn = {
  field: 'size_KB',
  type: 'number',
  title: 'اندازه'
};
export const altColumn = {
  field: 'alt',
  type: 'string',
  title: 'متن جایگزین تصویر'
};
